/**
 * Helpers WebAuthn (Sprint 59): conversión base64url ↔ ArrayBuffer y mapeo de
 * las opciones JSON del backend a los tipos del navegador. Sin dependencias para
 * evitar ciclos de import con auth.ts / backend.ts.
 */

export function b64urlToBuf(value: string): ArrayBuffer {
  const pad = value.length % 4 === 0 ? "" : "=".repeat(4 - (value.length % 4));
  const base64 = (value + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export function bufToB64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1)
    bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

interface CredentialDescriptorJSON {
  type: "public-key";
  id: string;
}

export interface CreationOptionsJSON {
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: { type: "public-key"; alg: number }[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: CredentialDescriptorJSON[];
}

export function creationOptionsFromJSON(
  json: CreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  const options: PublicKeyCredentialCreationOptions = {
    rp: json.rp,
    user: {
      id: b64urlToBuf(json.user.id),
      name: json.user.name,
      displayName: json.user.displayName,
    },
    challenge: b64urlToBuf(json.challenge),
    pubKeyCredParams: json.pubKeyCredParams,
    excludeCredentials: (json.excludeCredentials ?? []).map((c) => ({
      type: "public-key" as const,
      id: b64urlToBuf(c.id),
    })),
  };
  if (json.timeout !== undefined) options.timeout = json.timeout;
  if (json.attestation) options.attestation = json.attestation;
  if (json.authenticatorSelection)
    options.authenticatorSelection = json.authenticatorSelection;
  return options;
}

export interface RequestOptionsJSON {
  challenge: string;
  rpId: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: CredentialDescriptorJSON[];
}

export function requestOptionsFromJSON(
  json: RequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  const options: PublicKeyCredentialRequestOptions = {
    challenge: b64urlToBuf(json.challenge),
    allowCredentials: (json.allowCredentials ?? []).map((c) => ({
      type: "public-key" as const,
      id: b64urlToBuf(c.id),
    })),
  };
  if (json.rpId) options.rpId = json.rpId;
  if (json.timeout !== undefined) options.timeout = json.timeout;
  if (json.userVerification) options.userVerification = json.userVerification;
  return options;
}

export function isPasskeySupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.credentials
  );
}
